import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { EnableSignUpSessionRecording } from '~/constants/ff.posthog';
import { useFeatureFlag } from './useFeatureFlag';
import { usePosthog } from './usePosthog';

const SIGNUP_PATH = '/signup';

export const useSignUpPosthogSessionRecording = () => {
  const location = useLocation();
  const posthog = usePosthog();
  const isRecordingRef = useRef(false);
  const hasStartedSignupRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const isEnabled = useFeatureFlag(EnableSignUpSessionRecording);

  useEffect(() => {
    // PostHog rotates the session id on reset() (called on logout) and on session
    // timeout. The recording state below is scoped to a single session, so re-arm it
    // whenever the session changes — otherwise a second sign up in the same tab (after
    // logging out of the first) would never start a new recording.
    const currentSessionId = posthog.getSessionId();
    if (currentSessionId !== sessionIdRef.current) {
      sessionIdRef.current = currentSessionId;
      isRecordingRef.current = false;
      hasStartedSignupRef.current = false;
    }

    const isOnSignupFlow = location.pathname.startsWith(SIGNUP_PATH);

    // Track if the user entered sign-up during this session.
    if (isEnabled && isOnSignupFlow) {
      hasStartedSignupRef.current = true;
    }

    // Keep recording after the user leaves sign-up to capture the first-time experience.
    const shouldRecord = isEnabled && hasStartedSignupRef.current;

    if (shouldRecord && !isRecordingRef.current) {
      posthog.startSessionRecording();
      isRecordingRef.current = true;
    }
  }, [location.pathname, posthog, isEnabled]);

  // Stop recording on unmount only (when browser/tab closes)
  useEffect(() => {
    return () => {
      posthog.stopSessionRecording();
    };
  }, [posthog]);
};
